import { environment } from '@env/environment';
import { SiteSettingValue } from '@app/interfaces/common/site-setting.interfaces';

export const DEFAULT_PERSON_PLACEHOLDER_ASSET = '/assets/placeholders/person-avatar.svg';
export const DEFAULT_IMAGE_PLACEHOLDER_ASSET = '/assets/placeholders/catalog-image.svg';

const apiBaseUrl = environment.apiUrl.replace(/\/api\/?$/, '');

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseJsonValue(rawValue: string): unknown {
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return null;
  }

  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
}

export function getSettingText(rawValue: unknown, fallback: string): string {
  if (typeof rawValue === 'string') {
    return rawValue.trim().length > 0 ? rawValue : fallback;
  }

  if (typeof rawValue === 'number' || typeof rawValue === 'boolean') {
    return String(rawValue);
  }

  return fallback;
}

export function getSettingArray<T>(rawValue: unknown, fallback: T[]): T[] {
  if (Array.isArray(rawValue)) {
    return rawValue as T[];
  }

  if (typeof rawValue === 'string') {
    const parsed = parseJsonValue(rawValue);
    return Array.isArray(parsed) ? (parsed as T[]) : fallback;
  }

  return fallback;
}

export function getSettingObject<T>(rawValue: unknown, fallback: T): T {
  if (isPlainObject(rawValue)) {
    return rawValue as T;
  }

  if (typeof rawValue === 'string') {
    const parsed = parseJsonValue(rawValue);
    return isPlainObject(parsed) ? (parsed as T) : fallback;
  }

  return fallback;
}

export function resolveAssetUrl(path: unknown, fallback: string = ''): string {
  const rawPath = typeof path === 'string' ? path.trim() : '';
  if (!rawPath) {
    return fallback;
  }

  if (
    rawPath.startsWith('http://') ||
    rawPath.startsWith('https://') ||
    rawPath.startsWith('//') ||
    rawPath.startsWith('data:') ||
    rawPath.startsWith('blob:')
  ) {
    return rawPath;
  }

  // Preserve the legacy hero-slide upload endpoint used by the settings editor.
  const legacyHeroMatch = rawPath.match(/^\/?assets\/hero\/(hero-slide-[A-Za-z0-9._-]+)$/);
  if (legacyHeroMatch) {
    return `${apiBaseUrl}/api/public/settings/hero-slide/${legacyHeroMatch[1]}`;
  }

  if (rawPath.startsWith('assets/') || rawPath.startsWith('/assets/')) {
    return rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
  }

  return rawPath.startsWith('/') ? `${apiBaseUrl}${rawPath}` : `${apiBaseUrl}/${rawPath}`;
}

export function normalizeExternalUrl(rawValue: unknown, fallback: string = '#'): string {
  const rawUrl = typeof rawValue === 'string' ? rawValue.trim() : '';
  if (!rawUrl) {
    return fallback;
  }

  if (
    rawUrl.startsWith('http://') ||
    rawUrl.startsWith('https://') ||
    rawUrl.startsWith('mailto:') ||
    rawUrl.startsWith('tel:') ||
    rawUrl.startsWith('#') ||
    rawUrl.startsWith('/')
  ) {
    return rawUrl;
  }

  return `https://${rawUrl}`;
}
